// apps/patient/src/components/appointments/manageTypes.ts
// Shapes returned by the patient manage endpoints (portal and reminder link).

export interface ManageProvider {
  id: string;
  first_name: string;
  last_name: string;
  credentials?: string;
}

export interface ManageEligibleProvider extends ManageProvider {
  schedulable_location_ids?: string[];
}

export interface ManageLocationAddress {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

export interface ManageLocation {
  id: string;
  name: string;
  address: ManageLocationAddress | string | null;
}

// organization_location.address is a JSON object; render it as one line.
export function formatLocationAddress(address: ManageLocation['address']): string {
  if (!address) return '';
  if (typeof address === 'string') return address;
  const stateZip = [address.state, address.zip].filter(Boolean).join(' ');
  return [address.line1, address.line2, address.city, stateZip]
    .filter((part) => part && String(part).trim() !== '')
    .join(', ');
}

export interface ManageAppointmentInfo {
  organization_id: string;
  organization_name: string;
  organization_phone: string;
  appointment_date: string;
  start_time: string;
  end_time: string;
  timezone: string;
  start_timestamp: number;
  duration_minutes: number;
  is_telehealth: boolean;
  status: string;
  provider: ManageProvider;
  location: ManageLocation | null;
  appointment_type: { id: string; name: string; duration_minutes: number } | null;
  /** "link" (reminder link) or "portal". */
  channel?: string;
  /** Open, in the future and outside the cancellation window. */
  can_manage?: boolean;
  inside_window?: boolean;
  /** YYYY-MM-DD of the patient's OTHER open appointments in this org (reschedule same-day / next-day notice). */
  other_appointment_dates?: string[];
  /** can_manage AND the action is offered on this channel (settings.self_service_link_actions). */
  can_cancel: boolean;
  can_reschedule: boolean;
  cancellation_window_hours: number;
  same_provider_only: boolean;
  settings: Record<string, any>;
  eligible_providers: ManageEligibleProvider[];
  locations: ManageLocation[];
}

export interface RescheduleBody {
  provider_id: string;
  location_id: string | null;
  appointment_date: string;
  start_time: string;
  start_timestamp: number;
  timezone: string;
}

/** Body of the "join the waitlist" endpoints (link and portal). */
export interface WaitlistJoinBody {
  provider_mode: 'current' | 'any';
  location_id: string | null;
  time_preferences: { windows: { start: number; end: number | null }[]; excluded_days: number[] };
  earliest_date: string | null;
  latest_date: string | null;
  comments: string | null;
}

/**
 * GET/POST public/waitlist-offer/{token}: one offered slot from the waitlist
 * auto-offer. Trimmed payload (slot + practice), never patient identifiers.
 * reason: '' | 'already_booked' | 'expired' | 'filled' | 'unavailable'.
 */
export interface WaitlistOfferInfo {
  claimed: boolean;
  can_claim: boolean;
  reason: string;
  organization_name: string;
  organization_phone: string;
  provider: ManageProvider | null;
  location: { name: string; address: ManageLocation['address'] } | null;
  appointment_date: string;
  start_time: string;
  end_time: string;
  start_timestamp: number;
  timezone: string;
  duration_minutes: number;
  appointment_type_name: string;
}

export const formatProviderName = (p?: ManageProvider | null): string => {
  if (!p) return '';
  const name = `${p.first_name || ''} ${p.last_name || ''}`.trim();
  return p.credentials ? `${name}, ${p.credentials}` : name;
};

/** "Location changed: A to B" style callout, or null when unchanged. */
export const describeChange = (label: string, from: string, to: string): string | null => {
  if (!from || !to || from === to) return null;
  return `${label} changed: ${from} to ${to}`;
};

/** Reads the server's `message` from a failed request, falling back to err.message or the given text. */
export const readErrorMessage = async (err: any, fallback: string): Promise<string> => {
  try {
    if (err?.response && typeof err.response.json === 'function') {
      const body = await err.response.json();
      if (body && typeof body.message === 'string' && body.message.trim()) {
        return body.message;
      }
    }
  } catch {
    // body not JSON or already consumed
  }
  if (err?.message && !/^HTTP error/.test(err.message)) {
    return err.message;
  }
  return fallback;
};
